import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://public.readdy.ai/ai/img_res/32e93ee1-66f6-45ea-acb1-b460cccb184e.png" 
              alt="ARMIR SH.P.K. Logo" 
              className="h-12 w-12 object-contain"
            />
            <h1 className="text-2xl font-bold text-gray-900">ARMIR SH.P.K.</h1>
          </div>
          <div className="flex items-center gap-8">
            <button onClick={() => scrollToSection('products')} className="text-sm font-light text-gray-600 hover:text-black transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <span className="text-gray-300">|</span>
            <button onClick={() => scrollToSection('about')} className="text-sm font-light text-gray-600 hover:text-black transition-colors cursor-pointer whitespace-nowrap">About</button>
            <span className="text-gray-300">|</span>
            <button onClick={() => scrollToSection('features')} className="text-sm font-light text-gray-600 hover:text-black transition-colors cursor-pointer whitespace-nowrap">Features</button>
            <span className="text-gray-300">|</span>
            <button onClick={() => scrollToSection('contact')} className="text-sm font-light text-gray-600 hover:text-black transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-24 pb-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #FAFAF8 0%, #F5F1E8 100%)' }}>
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%239C92AC\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        
        <div className="max-w-7xl mx-auto px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-3">
              <div className="space-y-4">
                <h2 className="text-7xl font-light text-gray-900 leading-tight">Modern iOS</h2>
                <h2 className="text-8xl font-bold text-gray-900 leading-tight">Applications</h2>
                <h2 className="text-6xl font-normal text-gray-800 leading-tight">For Enhanced Security</h2>
              </div>
              
              <div className="mt-16 max-w-xl">
                <p className="text-lg font-normal text-gray-600 leading-relaxed">
                  Expert team developing VPN and Cleaner solutions
                </p>
                <p className="text-lg font-normal text-gray-600 leading-relaxed mt-2">
                  Elevating mobile device security and user experience
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 flex justify-center">
              <div className="relative w-96 h-96">
                <div className="absolute inset-0 rounded-full" style={{ 
                  background: 'linear-gradient(135deg, #FFB4A3 0%, #FF6B4A 50%, #6B4E9A 100%)',
                  filter: 'blur(40px)',
                  opacity: 0.6,
                  animation: 'float 6s ease-in-out infinite'
                }}></div>
                <div className="absolute inset-8 rounded-full" style={{ 
                  background: 'linear-gradient(135deg, #FFB4A3 0%, #FF6B4A 50%, #6B4E9A 100%)',
                  boxShadow: '0 24px 64px rgba(0,0,0,0.15)'
                }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-20">
            <h3 className="text-6xl font-serif text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Our Products</h3>
            <div className="w-16 h-px bg-orange-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* VPN Card */}
            <div className="rounded-3xl p-12 h-[520px] flex flex-col" style={{ background: 'linear-gradient(135deg, #1F1F1F 0%, #2A2A2A 100%)' }}>
              <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-white text-3xl"></i>
              </div>
              <h4 className="text-5xl font-bold text-white mb-6">VPN</h4>
              <p className="text-gray-300 text-base leading-7 flex-grow">
                Secure connection technology protecting your privacy across all networks with military-grade encryption
              </p>
              <p className="text-xs text-gray-600 mt-6">AES-256 encryption standard</p>
            </div>

            {/* Cleaner Card */}
            <div className="rounded-3xl h-[520px] relative overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/86a4d302484bb29d908652812f8ce495.jpg" 
                alt="Cleaner Product" 
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30"></div>
              <div className="absolute top-12 left-12 backdrop-blur-xl bg-white/10 rounded-full px-6 py-3 flex items-center gap-3">
                <i className="ri-brush-line text-white text-lg"></i>
                <span className="text-white text-sm font-semibold tracking-widest uppercase">Cleaner</span>
              </div>
              <div className="absolute bottom-12 left-12 right-12">
                <h4 className="text-4xl font-bold text-white" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
                  Optimize device performance instantly
                </h4>
              </div>
            </div>

            {/* Technology Card */}
            <div className="rounded-3xl p-12 h-[520px] bg-white shadow-lg flex flex-col">
              <h4 className="text-3xl font-serif text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Built with cutting-edge iOS frameworks
              </h4>
              <p className="text-sm text-gray-600 leading-6 mb-8">
                Swift 5.9 | SwiftUI | Combine framework
              </p>
              <div className="flex-grow flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=elegant%20iOS%20Swift%20code%20snippet%20on%20screen%20with%20modern%20development%20environment%20showing%20clean%20syntax%20highlighting%20in%20professional%20workspace%20minimal%20aesthetic%20soft%20lighting&width=320&height=280&seq=tech-code-display&orientation=landscape" 
                  alt="iOS Development" 
                  className="w-full h-64 object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32" style={{ background: '#FFF5F2' }}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="relative" style={{ background: '#FFF5F2' }}>
              <div className="absolute top-12 left-12 z-10">
                <span className="text-xs font-light tracking-widest uppercase text-gray-600">INNOVATION</span>
              </div>
              <div className="relative h-full min-h-[600px] flex items-end pb-12 pl-12">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iPhone%20device%20mockup%20displaying%20security%20shield%20icon%20and%20VPN%20interface%20with%20clean%20minimalist%20design%20floating%20on%20soft%20peach%20background%20professional%20product%20photography%20elegant%20composition&width=600&height=700&seq=about-device-mockup&orientation=portrait" 
                  alt="iOS Security" 
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: 'top' }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20"></div>
                <div className="relative z-10 space-y-2">
                  <h3 className="text-6xl font-light text-gray-900 leading-tight">Enhanced</h3>
                  <h3 className="text-7xl font-bold text-gray-900 leading-tight">Mobile</h3>
                  <h3 className="text-6xl font-medium text-gray-900 leading-tight">Security</h3>
                </div>
              </div>
            </div>

            <div className="bg-white flex flex-col justify-center px-20 py-16">
              <h3 className="text-5xl font-bold text-gray-900 text-center leading-tight mb-12">
                Protecting Your<br />Digital Life
              </h3>
              <p className="text-base font-normal text-gray-600 leading-8 text-center max-w-md mx-auto mb-16">
                Our expert team develops modern applications for the iOS platform. VPN and Cleaner products are designed to enhance user experience and improve the security of mobile devices through innovative technology solutions.
              </p>
              <div className="flex justify-center">
                <div className="bg-gray-800 rounded-full px-10 py-4 flex items-center gap-3 hover:scale-105 transition-transform cursor-pointer">
                  <span className="text-white text-sm font-medium whitespace-nowrap">Explore Technology</span>
                  <i className="ri-arrow-right-line text-white text-lg"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-20">
            <h3 className="text-6xl font-serif text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Key Features</h3>
            <div className="w-16 h-px bg-orange-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center mx-auto mb-8">
                <i className="ri-lock-line text-white text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Military-Grade Encryption</h4>
              <p className="text-gray-600 leading-7">
                Advanced AES-256 encryption ensures your data remains secure and private across all network connections
              </p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center mx-auto mb-8">
                <i className="ri-speed-line text-white text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Performance Optimization</h4>
              <p className="text-gray-600 leading-7">
                Intelligent cleaning algorithms remove unnecessary files and optimize device performance for faster operation
              </p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mx-auto mb-8">
                <i className="ri-user-smile-line text-white text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">User-Friendly Interface</h4>
              <p className="text-gray-600 leading-7">
                Intuitive design built with SwiftUI provides seamless user experience with modern iOS design patterns
              </p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mx-auto mb-8">
                <i className="ri-global-line text-white text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Global Server Network</h4>
              <p className="text-gray-600 leading-7">
                Access secure VPN servers worldwide for optimal connection speed and reliable privacy protection
              </p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mx-auto mb-8">
                <i className="ri-refresh-line text-white text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Automatic Updates</h4>
              <p className="text-gray-600 leading-7">
                Regular updates ensure you always have the latest security features and performance improvements
              </p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center mx-auto mb-8">
                <i className="ri-customer-service-line text-white text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">24/7 Support</h4>
              <p className="text-gray-600 leading-7">
                Dedicated support team available around the clock to assist with any questions or technical issues
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24" style={{ background: '#1A2F2A' }}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <img 
                  src="https://public.readdy.ai/ai/img_res/32e93ee1-66f6-45ea-acb1-b460cccb184e.png" 
                  alt="ARMIR SH.P.K. Logo" 
                  className="h-8 w-8 object-contain"
                />
                <h4 className="text-xl font-semibold text-gray-200">ARMIR SH.P.K.</h4>
              </div>
              <p className="text-sm font-light text-gray-400 leading-6">
                Expert iOS Development<br />
                Modern Security Solutions<br />
                Trusted Worldwide
              </p>
            </div>

            <div>
              <h5 className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-5">Location</h5>
              <div className="space-y-2 text-sm text-gray-300">
                <p className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-gray-500 mt-1"></i>
                  <span>Lagjja nr.14</span>
                </p>
                <p className="ml-6">Rr. Fatmir Haxhi Hyseni</p>
                <p className="ml-6">NP 5/2903, ZK 8515</p>
                <p className="ml-6">DURRES, 2007</p>
                <p className="ml-6">Albania</p>
              </div>
            </div>

            <div>
              <h5 className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-5">Reach Us</h5>
              <div className="space-y-4">
                <a href="mailto:help@armirshpk.com" className="flex items-center gap-3 text-sm text-white hover:text-orange-400 transition-colors cursor-pointer">
                  <i className="ri-mail-line text-gray-500 text-base"></i>
                  <span>help@armirshpk.com</span>
                </a>
                <a href="tel:+12707871638" className="flex items-center gap-3 text-sm text-white hover:text-orange-400 transition-colors cursor-pointer">
                  <i className="ri-phone-line text-gray-500 text-base"></i>
                  <span>+1 2707871638</span>
                </a>
              </div>
            </div>

            <div>
              <h5 className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-5">Products</h5>
              <div className="space-y-4">
                <p className="text-sm text-gray-300">VPN Application</p>
                <p className="text-sm text-gray-300">Cleaner Tool</p>
                <p className="text-sm text-gray-300">iOS Solutions</p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden pt-16">
            <div className="text-9xl font-black tracking-tighter text-gray-100 opacity-15 select-none" style={{ fontSize: '180px', lineHeight: '1', letterSpacing: '-8px' }}>
              ARMIR
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&display=swap');
      `}</style>
    </div>
  );
}
